import * as React from 'react';
import { Dropdown as BootstrapDropdown } from 'react-bootstrap';

import { bemBlock, bemElement } from 'af-utils/bem.util';

import { Button } from '@acceligentllc/storybook';

import Radio from 'af-components/Controls/Radio';
import Label from 'af-components/LockedValue/Label';

import styles from './styles.module.scss';

const MINUTES_IN_DAY = 24 * 60;
const MINUTES_IN_HOUR = 60;
const NOON_HOUR = 12;

const _formatTwoDigits = (value: number): string => value < 10 ? `0${value}` : `${value}`;

const _toDisplayHours = (hours: number): number => {
	if (hours > NOON_HOUR) {
		return hours - NOON_HOUR;
	}

	return hours || NOON_HOUR;
};

interface OwnProps {
	/** minutes from midnight, or null when unset */
	value: Nullable<number>;
	onChange: (value: Nullable<number>) => void;
	id: string;
	label?: string;
	tooltipMessage?: string;
	disabled?: boolean;
	drop?: 'left' | 'right' | 'up' | 'down';
	withCaret?: boolean;
	className?: string;
	containerClassName?: string;
	placeholder?: string;
	error?: string;
	warning?: string;
	minInterval?: number;
}

type Props = OwnProps;

const TimeInput: React.FC<Props> = (props) => {
	const {
		value,
		onChange,
		id,
		label,
		tooltipMessage,
		disabled,
		drop,
		withCaret = true,
		className,
		placeholder = '00:00',
		error,
		warning,
		minInterval = 1,
	} = props;

	const [isOpen, setIsOpen] = React.useState(false);
	const [hours, setHours] = React.useState(0);
	const [minutes, setMinutes] = React.useState(0);

	React.useEffect(() => {
		if (!isOpen) {
			return;
		}

		setHours(value === null || value === undefined ? 0 : Math.floor(value / MINUTES_IN_HOUR));
		setMinutes(value === null || value === undefined ? 0 : value % MINUTES_IN_HOUR);
	}, [isOpen, value]);

	const isAM = hours < NOON_HOUR;

	const onToggle = React.useCallback((nextIsOpen: boolean, _event?, meta?) => {
		if (meta?.source === 'select') {
			return;
		}

		setIsOpen(nextIsOpen);
	}, []);

	const handleTimeInputFocus = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
		event.currentTarget.select();
	}, []);

	const handleHoursChange = React.useCallback(({ currentTarget: { value: hoursValue } }: React.ChangeEvent<HTMLInputElement>) => {
		let nextHours = parseInt(hoursValue);

		if (!nextHours || nextHours < 0 || nextHours > NOON_HOUR) {
			nextHours = 0;
		}

		if (nextHours === NOON_HOUR) {
			nextHours = 0;
		}

		setHours((currentHours) => currentHours >= NOON_HOUR ? nextHours + NOON_HOUR : nextHours);
	}, []);

	const handleMinutesChange = React.useCallback(({ currentTarget: { value: minutesValue } }: React.ChangeEvent<HTMLInputElement>) => {
		setMinutes((parseInt(minutesValue) || 0) % MINUTES_IN_HOUR);
	}, []);

	const handleMinutesBlur = React.useCallback(() => {
		const _minutes = Math.round(minutes / minInterval) * minInterval % 60;
		if (hours === 24 && _minutes !== 0) {
			setHours(23);
		}
		setMinutes(_minutes);
	}, [hours, minutes, minInterval]);

	const hourIncrement = React.useCallback(() => setHours((current) => (current + 1) % 24), []);
	const hourDecrement = React.useCallback(() => setHours((current) => (current + 23) % 24), []);

	const minuteIncrement = React.useCallback(() => {
		const nextMinutes = (minutes + minInterval) % 60;
		setMinutes(nextMinutes);
		if (nextMinutes !== 0 && hours === 24) {
			setHours(23);
		}
	}, [hours, minInterval, minutes]);

	const minuteDecrement = React.useCallback(() => {
		const nextMinutes = (minutes + (60 - minInterval)) % 60;
		setMinutes(nextMinutes);
		if (nextMinutes !== 0 && hours === 24) {
			setHours(23);
		}
	}, [hours, minInterval, minutes]);

	const setAM = React.useCallback(() => setHours((current) => current >= NOON_HOUR ? current - NOON_HOUR : current), []);
	const setPM = React.useCallback(() => setHours((current) => current < NOON_HOUR ? current + NOON_HOUR : current), []);

	const handleCurrentTime = React.useCallback(() => {
		const now = new Date();
		const totalMinutes = now.getHours() * MINUTES_IN_HOUR + now.getMinutes();
		const snapped = totalMinutes - (totalMinutes % minInterval);

		onChange(snapped);
		setIsOpen(false);
	}, [minInterval, onChange]);

	const handleSave = React.useCallback(() => {
		onChange((hours * MINUTES_IN_HOUR + minutes) % MINUTES_IN_DAY);
		setIsOpen(false);
	}, [hours, minutes, onChange]);

	const handleClear = React.useCallback(() => {
		onChange(null);
		setIsOpen(false);
	}, [onChange]);

	const renderSelected = React.useCallback(() => {
		if (value === null || value === undefined) {
			return placeholder;
		}

		const valueHours = Math.floor(value / MINUTES_IN_HOUR);
		const displayHours = valueHours % NOON_HOUR === 0 ? NOON_HOUR : valueHours % NOON_HOUR;
		const suffix = valueHours < NOON_HOUR ? 'AM' : 'PM';

		return `${_formatTwoDigits(displayHours)}:${_formatTwoDigits(value % MINUTES_IN_HOUR)} ${suffix}`;
	}, [value, placeholder]);

	const fieldClassName = React.useMemo(() => {
		let result = 'dropdown-field';

		result = className ? `${result} ${className}` : result;
		result = error ? `${result} dropdown-field--error` : result;

		return result;
	}, [className, error]);

	const dropdownMenuClassName = bemBlock('dropdown-menu', { 'scroll-container': true });
	const dropdownToggleClassName = bemBlock('dropdown-toggle', { 'with-caret': !!withCaret });

	return (
		<>
			{
				!!label &&
				<div className="input-header">
					<Label
						label={label}
						tooltipMessage={tooltipMessage}
						tooltipPlacement="top"
						withMargin={true}
					/>
				</div>
			}
			<BootstrapDropdown
				className={fieldClassName}
				drop={drop ?? 'right'}
				id={id}
				onToggle={onToggle}
				show={isOpen}
			>
				<BootstrapDropdown.Toggle
					className={`${dropdownToggleClassName} ${styles.dropdownButton}`}
					disabled={disabled}
				>
					<div className="dropdown-menu__selected">
						{renderSelected()}
					</div>
					{withCaret && <span className="icon-down"></span>}
				</BootstrapDropdown.Toggle>
				<BootstrapDropdown.Menu className={dropdownMenuClassName}>
					<div className="time-picker">
						<div className={bemElement('time-picker', 'controls')}>
							<div className={bemElement('time-picker', 'controls__time-input-container')}>
								<span
									className={`${bemElement('time-picker', 'controls__shift-control')} icon-up`}
									onClick={hourIncrement}
								/>
								<input
									className={bemElement('time-picker', 'controls__time-input')}
									onChange={handleHoursChange}
									onFocus={handleTimeInputFocus}
									value={_formatTwoDigits(_toDisplayHours(hours))}
								/>
								<span
									className={`${bemElement('time-picker', 'controls__shift-control')} icon-down`}
									onClick={hourDecrement}
								/>
							</div>
							<div className={bemElement('time-picker', 'controls__spacer')}>:</div>
							<div className={bemElement('time-picker', 'controls__time-input-container')}>
								<span
									className={`${bemElement('time-picker', 'controls__shift-control')} icon-up`}
									onClick={minuteIncrement}
								/>
								<input
									className={bemElement('time-picker', 'controls__time-input')}
									onBlur={handleMinutesBlur}
									onChange={handleMinutesChange}
									onFocus={handleTimeInputFocus}
									value={_formatTwoDigits(minutes)}
								/>
								<span
									className={`${bemElement('time-picker', 'controls__shift-control')} icon-down`}
									onClick={minuteDecrement}
								/>
							</div>
							<div className={bemElement('time-picker', 'controls__ampm')}>
								<Radio
									checked={isAM}
									disabled={disabled}
									label="AM"
									onCheck={setAM}
								/>
								<Radio
									checked={!isAM}
									disabled={disabled}
									label="PM"
									onCheck={setPM}
								/>
							</div>
						</div>
						<div className={bemElement('time-picker', 'current-time-button')}>
							<Button
								icon="CurrentTime"
								label="Use Current Time"
								onClick={handleCurrentTime}
								style="link"
							/>
						</div>
						<div className={bemElement('time-picker', 'options')}>
							<Button
								label="Clear"
								onClick={handleClear}
								style="secondary"
							/>
							<Button
								label="Save"
								onClick={handleSave}
								style="primary"
							/>
						</div>
					</div>
				</BootstrapDropdown.Menu>
			</BootstrapDropdown>
			{
				(
					(error && <span className="help-block"><span className="icon-info" /> {error}</span>) ??
					(warning && <span className="help-block text-orange"><span className="icon-info" /> {warning}</span>)
				)
			}
		</>
	);
};

export default React.memo(TimeInput);
